import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Timestamp } from 'firebase/firestore';

import { AppContext } from '../Context';
import { db } from '../firebase/firebase';
import {
  cargarProgresoHistorico,
  combinarProgresoEvaluaciones,
} from '../utils/progresoEvaluacionesHistorico';

const ESTADO_COMPLETADO = 'completado';

const useProgresoEvaluaciones = (nivel, { habilitado = true } = {}) => {
  const { currentUser } = useContext(AppContext);
  const [progreso, setProgreso] = useState(currentUser?.progresoEvaluaciones || {});
  const [errorProgreso, setErrorProgreso] = useState('');
  const historicoConsultadoRef = useRef('');
  const progresoRef = useRef(currentUser?.progresoEvaluaciones || {});

  useEffect(() => {
    if (!habilitado) {
      progresoRef.current = {};
      setProgreso({});
      setErrorProgreso('');
      historicoConsultadoRef.current = '';
      return;
    }

    const progresoActual = currentUser?.progresoEvaluaciones || {};
    progresoRef.current = progresoActual;
    setProgreso(progresoActual);
  }, [currentUser?.progresoEvaluaciones, habilitado]);

  useEffect(() => {
    const userId = currentUser?.uid;
    if (!habilitado) return undefined;
    const consultaKey = userId ? `${userId}_${nivel}` : '';
    if (!consultaKey || historicoConsultadoRef.current === consultaKey) return undefined;
    historicoConsultadoRef.current = consultaKey;

    let activo = true;
    const reconciliarHistorico = async () => {
      try {
        const historicoCompleto = await cargarProgresoHistorico(userId);
        if (!activo) return;

        const historicoNivel = Object.fromEntries(
          Object.entries(historicoCompleto).filter(([, registro]) => registro.nivel === nivel)
        );
        const actual = progresoRef.current;
        const combinado = combinarProgresoEvaluaciones(actual, historicoNivel);

        progresoRef.current = combinado;
        setProgreso(combinado);
      } catch (error) {
        console.error('Error reconciliando evaluaciones históricas:', error);
        historicoConsultadoRef.current = '';
        if (activo) setErrorProgreso('No fue posible revisar las respuestas anteriores del alumno.');
      }
    };

    reconciliarHistorico();
    return () => { activo = false; };
  }, [currentUser?.uid, habilitado, nivel]);

  const guardarEstado = useCallback(async (ejercicio, modulo, estado) => {
    if (!habilitado) return false;

    if (!currentUser?.uid || !db) {
      setErrorProgreso('No fue posible identificar al alumno para guardar el avance.');
      return false;
    }

    const anterior = progresoRef.current[ejercicio.clave] || {};
    const ahora = Timestamp.now();
    const sigueCompletado = anterior.estado === ESTADO_COMPLETADO;
    const estadoFinal = sigueCompletado ? ESTADO_COMPLETADO : estado;
    const registro = {
      ...anterior,
      clave: ejercicio.clave,
      nivel,
      modulo,
      ejercicioId: ejercicio.id,
      nombre: ejercicio.nombre,
      estado: estadoFinal,
      porcentaje: estadoFinal === ESTADO_COMPLETADO ? 100 : 0,
      intentos: Number(anterior.intentos || 0) + (estado === 'en-curso' ? 1 : 0),
      iniciadoEn: anterior.iniciadoEn || ahora,
      ultimoAccesoEn: ahora,
      actualizadoEn: ahora,
      ...(estadoFinal === ESTADO_COMPLETADO && {
        completadoEn: anterior.completadoEn || ahora,
      }),
    };

    setProgreso((actual) => {
      const siguiente = { ...actual, [ejercicio.clave]: registro };
      progresoRef.current = siguiente;
      return siguiente;
    });
    setErrorProgreso('');

    // La entrega guardada en `evaluaciones` es la fuente de verdad. El estado
    // optimista mantiene la interfaz actualizada y se reconstruye desde esas
    // entregas al volver a ingresar, sin exigir una segunda escritura en el
    // documento del usuario (que puede tener reglas de acceso diferentes).
    return true;
  }, [currentUser?.uid, habilitado, nivel]);

  const iniciarEjercicio = useCallback(
    (ejercicio, modulo) => guardarEstado(ejercicio, modulo, 'en-curso'),
    [guardarEstado]
  );

  const completarEjercicio = useCallback(
    (ejercicio, modulo) => guardarEstado(ejercicio, modulo, ESTADO_COMPLETADO),
    [guardarEstado]
  );

  return { progreso, iniciarEjercicio, completarEjercicio, errorProgreso };
};

export default useProgresoEvaluaciones;
